import React, { useEffect, useRef, useState } from 'react';

import { useSelector } from 'react-redux';

import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { Hide } from '@atlaskit/primitives/responsive';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { PullRequestState } from 'src/components/types/src/pull-request';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { getIsSingleFileModeActive } from 'src/redux/pull-request/selectors';

import BranchesAndState from '../../branches-and-state';
import { HeaderActions } from '../../header-actions';
import { DiffTabHeader } from '../diff/diff-tab-header';
import { useSelectedTab } from '../hooks/use-selected-tab';
import { PRTabsContainer } from '../pr-tabs-container';

type CompactHeaderProps = {
  title: string;
  state: PullRequestState;
  draft: boolean;
  createdOn: string;
  closedOn: string | null | undefined;
  isLoadingConflicts: boolean;
  cssVarContainerRef: React.RefObject<HTMLDivElement | null>;
};

const compactHeaderWrapper = xcss({
  backgroundColor: 'elevation.surface',
  paddingInlineStart: 'space.500',
  paddingInlineEnd: 'space.500',
  paddingBlock: 'space.100',
  boxShadow: 'elevation.shadow.overflow',
});

export const CompactHeader = ({
  title,
  state,
  draft,
  createdOn,
  closedOn,
  isLoadingConflicts,
  cssVarContainerRef,
}: CompactHeaderProps) => {
  const [isSmallHeightScreen, setIsSmallHeightScreen] = useState(false);
  const isSingleFileModeActive = useSelector(getIsSingleFileModeActive);
  const selectedTab = useSelectedTab();
  const diffViewNavigationTabsNewThresholdEnabled = useStatsigGate(
    StatsigFeatureKeys.diffViewNavigationTabsNewThresholdEnabled
  );

  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const heightObserver = new ResizeObserver(entries => {
      entries.forEach(entry => {
        cssVarContainerRef.current?.style.setProperty(
          '--pr-compact-header-bottom',
          `${entry.target.getBoundingClientRect().bottom}px`
        );
      });
    });

    if (headerRef.current) {
      heightObserver.observe(headerRef.current);
    }

    return () => {
      heightObserver.disconnect();
    };
  }, [cssVarContainerRef]);

  useEffect(() => {
    const screenHeightThreshold = diffViewNavigationTabsNewThresholdEnabled
      ? 768
      : 1080;

    const heightObserver = new ResizeObserver(entries => {
      entries.forEach(entry => {
        setIsSmallHeightScreen(
          entry.contentRect.height < screenHeightThreshold
        );
      });
    });

    heightObserver.observe(document.body);

    return () => {
      heightObserver.disconnect();
    };
  }, [setIsSmallHeightScreen, diffViewNavigationTabsNewThresholdEnabled]);

  return (
    <Box ref={headerRef} xcss={compactHeaderWrapper}>
      <Stack space="space.100">
        <Inline spread="space-between" alignBlock="center" shouldWrap>
          <Hide below="md">
            <Stack space="space.075">
              <Hide below="lg">
                <Text weight="semibold">{title}</Text>
              </Hide>
              <BranchesAndState
                tabIndex={-1}
                pullRequestState={state}
                pullRequestDraft={draft}
                pullRequestCreatedOn={createdOn}
                pullRequestClosedOn={closedOn}
              />
            </Stack>
          </Hide>
          <HeaderActions isHeaderSticky includeDiffActions={false} />
        </Inline>
        {!isSmallHeightScreen && (
          <PRTabsContainer isLoadingConflicts={isLoadingConflicts} />
        )}
        {selectedTab === 'diff' && (
          <DiffTabHeader
            isSingleFileModeActive={isSingleFileModeActive}
            isStickyHeader
          />
        )}
      </Stack>
    </Box>
  );
};
